import React, { FC, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Box from "@material-ui/core/Box";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import HighlightOffIcon from "@material-ui/icons/HighlightOff";

import { SpwnApiResponse, SpwnService } from "@spwn-portal/grpc";

import { SquareLoader } from "components/atoms/loader/SquareLoader";
import { useConnectClient } from "utility/connectWeb";
import { use3DSecureStatus } from "./paw/use3DSecureStatus";

type PaymentAfterAuthenticationResult =
  | {
      status: "SPWN_API_RESPONSE_SUCCESS";
    }
  | {
      status: "SPWN_API_RESPONSE_ERROR";
      result: PaymentAfterAuthenticationResponseError;
    };

type PaymentAfterAuthenticationResponseError = {
  message: string; // エラーメッセージ
};

export const PaymentCompleted: FC = () => {
  const { search } = useLocation();
  const { postStatus } = use3DSecureStatus();

  const query = new URLSearchParams(search);
  const AccessID = query.get("AccessID");
  const AccessPass = query.get("AccessPass");

  const connectClient = useConnectClient(SpwnService);

  const [data, setData] =
    React.useState<PaymentAfterAuthenticationResult | null>(null);

  const fetchPaymentResult =
    React.useCallback(async (): Promise<PaymentAfterAuthenticationResult> => {
      try {
        if (!AccessID) {
          throw new Error("AccessID is null");
        }
        if (!AccessPass) {
          throw new Error("AccessPass is null");
        }

        const response = await connectClient.completePurchasePAWWith3DS({
          accessId: AccessID,
          accessPass: AccessPass,
        });

        if (response.status !== SpwnApiResponse.SUCCESS) {
          postStatus("error");
          throw new Error("Network response was not ok");
        }

        postStatus("success");

        return {
          status: "SPWN_API_RESPONSE_SUCCESS",
        };
      } catch (error) {
        console.error("fetchPaymentResult error:", error);
        return {
          status: "SPWN_API_RESPONSE_ERROR",
          result: {
            message: error instanceof Error ? error.message : "Unknown error",
          },
        };
      }
    }, [AccessID, AccessPass, connectClient, postStatus]);

  useEffect(() => {
    if (!data) {
      fetchPaymentResult().then(setData);
    }
  }, [data, fetchPaymentResult]);

  if (!data) {
    return (
      <Box
        display={"flex"}
        justifyContent="center"
        alignItems="center"
        minHeight="calc(100vh - 65px - 179px)"
        bgcolor={"#fff"}
      >
        <Box>
          <Box
            display={"flex"}
            justifyContent="center"
            alignItems="center"
            marginBottom={"16px"}
          >
            <SquareLoader />
          </Box>
          {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
          <Box textAlign={"center"}>
            決済中です
            {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
            <br />
            しばらくお待ちください
          </Box>
        </Box>
      </Box>
    );
  }

  if (data?.status === "SPWN_API_RESPONSE_ERROR") {
    return <PaymentErrorDisplay />;
  }

  return <PaymentCompletedDisplay />;
};

const PaymentCompletedDisplay: FC = () => {
  const history = useHistory();

  return (
    <Box
      display={"flex"}
      justifyContent="center"
      alignItems="center"
      minHeight="calc(100vh - 65px - 179px)"
      bgcolor={"#fff"}
    >
      <Box width={"95%"} textAlign={"center"}>
        <Box>
          <CheckCircleOutlineIcon
            style={{ color: "#00c2ae", fontSize: "80px" }}
          />
        </Box>
        {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
        <Box component={"h1"} marginTop={"16px"} fontSize={"24px"}>
          決済が完了しました
        </Box>

        <Box
          display={"flex"}
          flexDirection={"column"}
          alignItems={"center"}
          marginTop={"32px"}
          style={{
            gap: "16px",
          }}
        >
          <Box
            component={"button"}
            padding={"8px 16px"}
            fontSize={"16px"}
            bgcolor={"#00c2ae"}
            color={"#fff"}
            borderRadius={"4px"}
            border={"none"}
            style={{
              border: "2px solid #00c2ae",
              cursor: "pointer",
              outline: "none",
            }}
            onClick={() => {
              history.push("/account");
            }}
            // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
          >
            購入履歴を確認
          </Box>

          <Box
            component={"button"}
            padding={"8px 16px"}
            fontSize={"16px"}
            bgcolor={"#fff"}
            color={"#00c2ae"}
            borderRadius={"4px"}
            style={{
              border: "2px solid #00c2ae",
              cursor: "pointer",
              outline: "none",
            }}
            onClick={() => window.close()}
            // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
          >
            閉じる
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const PaymentErrorDisplay: FC = () => {
  return (
    <Box
      display={"flex"}
      justifyContent="center"
      alignItems="center"
      minHeight="calc(100vh - 65px - 179px)"
      bgcolor={"#fff"}
    >
      <Box width={"95%"} textAlign={"center"}>
        <Box>
          <HighlightOffIcon style={{ color: "#c20000", fontSize: "80px" }} />
        </Box>
        {/* eslint-disable-next-line no-restricted-syntax -- i18n 未対応 */}
        <Box component={"h1"} marginTop={"16px"} fontSize={"24px"}>
          決済に失敗しました
        </Box>
        <Box
          component={"p"}
          marginTop={"16px"}
          fontSize={"16px"}
          lineHeight={"1.5em"}
          // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
        >
          元のページに戻り、もう一度お試しください。
        </Box>

        <Box
          component={"button"}
          marginTop={"32px"}
          padding={"8px 16px"}
          fontSize={"16px"}
          bgcolor={"#fff"}
          color={"#00c2ae"}
          borderRadius={"4px"}
          style={{
            border: "2px solid #00c2ae",
            cursor: "pointer",
            outline: "none",
          }}
          onClick={() => window.close()}
          // eslint-disable-next-line no-restricted-syntax -- i18n 未対応
        >
          閉じる
        </Box>
      </Box>
    </Box>
  );
};
